import {
    SITE_BUILDER_INNER_BLOCK_TYPES,
} from "./innerContentBlocks";
import type {
    SiteBuilderInnerBlockType,
} from "./innerContentBlocks";

/**
 * Primary user-visible behavior owned by an inner block.
 *
 * The behavior is deliberately template-neutral. A renderer can choose its
 * markup and visual language, but it must preserve this behavior contract.
 */
export type SiteBuilderRuntimeBehaviorKind =
    | "commerce"
    | "content"
    | "disclosure"
    | "embed"
    | "media"
    | "navigation"
    | "search"
    | "sharing"
    | "submission"
    | "time";

/** Host capabilities are product-level; visual templates never own them. */
export type SiteBuilderRuntimeHost = "shop" | "web";

/** Independently testable runtime abilities required by canonical blocks. */
export type SiteBuilderRuntimeCapability =
    | "commerceAction"
    | "disclosureToggle"
    | "editorPreview"
    | "embedSource"
    | "formSubmission"
    | "linkNavigation"
    | "liveTime"
    | "mediaSource"
    | "nestedSlots"
    | "publicRender"
    | "searchQuery"
    | "shareTargets";

export type SiteBuilderRuntimeContractDefinition = Readonly<{
    behaviorKind: SiteBuilderRuntimeBehaviorKind;
    requiredCapabilities: readonly SiteBuilderRuntimeCapability[];
    supportedHosts: readonly SiteBuilderRuntimeHost[];
    type: SiteBuilderInnerBlockType;
}>;

/**
 * Capabilities implemented by one renderer, indexed by canonical block type.
 * Missing entries mean that the renderer does not support the block.
 */
export type SiteBuilderRuntimeSupport = Readonly<Partial<Record<
    SiteBuilderInnerBlockType,
    readonly SiteBuilderRuntimeCapability[]
>>>;

export type SiteBuilderRuntimeSupportGap = Readonly<{
    behaviorKind: SiteBuilderRuntimeBehaviorKind;
    missingCapabilities: readonly SiteBuilderRuntimeCapability[];
    type: SiteBuilderInnerBlockType;
}>;

const baseCapabilities = [
    "publicRender",
    "editorPreview",
] as const satisfies readonly SiteBuilderRuntimeCapability[];
const allRuntimeHosts = ["web", "shop"] as const satisfies readonly SiteBuilderRuntimeHost[];

function runtimeContract(
    type: SiteBuilderInnerBlockType,
    behaviorKind: SiteBuilderRuntimeBehaviorKind,
    capabilities: readonly SiteBuilderRuntimeCapability[] = [],
    supportedHosts: readonly SiteBuilderRuntimeHost[] = allRuntimeHosts,
): SiteBuilderRuntimeContractDefinition {
    return Object.freeze({
        behaviorKind,
        requiredCapabilities: Object.freeze([
            ...baseCapabilities,
            ...capabilities,
        ]),
        supportedHosts: Object.freeze([...supportedHosts]),
        type,
    });
}

/**
 * Canonical runtime contract for every block exposed by the shared sidebar.
 * `satisfies` makes adding a block without its runtime contract a type error.
 */
export const SITE_BUILDER_RUNTIME_CONTRACTS = Object.freeze({
    text: runtimeContract("text", "content"),
    card: runtimeContract("card", "navigation", [
        "nestedSlots",
        "linkNavigation",
    ]),
    button: runtimeContract("button", "navigation", ["linkNavigation"]),
    clientPortalButton: runtimeContract(
        "clientPortalButton",
        "navigation",
        ["linkNavigation"],
        ["web"],
    ),
    separator: runtimeContract("separator", "content"),
    image: runtimeContract("image", "media", ["mediaSource"]),
    video: runtimeContract("video", "media", [
        "mediaSource",
        "embedSource",
    ]),
    icon: runtimeContract("icon", "navigation", ["linkNavigation"]),
    accordion: runtimeContract("accordion", "disclosure", [
        "nestedSlots",
        "disclosureToggle",
    ]),
    alert: runtimeContract("alert", "content"),
    iconList: runtimeContract("iconList", "content"),
    rating: runtimeContract("rating", "content"),
    share: runtimeContract("share", "sharing", [
        "nestedSlots",
        "shareTargets",
    ]),
    socialMedia: runtimeContract("socialMedia", "sharing", [
        "nestedSlots",
        "shareTargets",
    ]),
    searchBar: runtimeContract("searchBar", "search", [
        "nestedSlots",
        "searchQuery",
    ]),
    textHighlight: runtimeContract("textHighlight", "content", [
        "nestedSlots",
    ]),
    chart: runtimeContract("chart", "content"),
    progressBar: runtimeContract("progressBar", "content"),
    badge: runtimeContract("badge", "content", ["nestedSlots"]),
    ctaBadge: runtimeContract("ctaBadge", "navigation", [
        "nestedSlots",
        "linkNavigation",
    ]),
    avatars: runtimeContract("avatars", "content"),
    blockquote: runtimeContract("blockquote", "content", ["nestedSlots"]),
    form: runtimeContract("form", "submission", [
        "nestedSlots",
        "formSubmission",
    ]),
    countdown: runtimeContract("countdown", "time", ["liveTime"]),
    code: runtimeContract("code", "embed", ["embedSource"]),
    map: runtimeContract("map", "embed", ["embedSource"]),
    donationButton: runtimeContract("donationButton", "navigation", [
        "linkNavigation",
    ]),
    addToCartButton: runtimeContract(
        "addToCartButton",
        "commerce",
        ["commerceAction"],
        ["shop"],
    ),
    supportedPaymentButton: runtimeContract("supportedPaymentButton", "content"),
} satisfies Record<
    SiteBuilderInnerBlockType,
    SiteBuilderRuntimeContractDefinition
>);

/** Stable, canonical order used by renderer completeness audits. */
export const SITE_BUILDER_RUNTIME_BLOCK_TYPES = Object.freeze([
    ...SITE_BUILDER_INNER_BLOCK_TYPES,
]) as readonly SiteBuilderInnerBlockType[];

export function siteBuilderRuntimeContract(
    type: string,
): SiteBuilderRuntimeContractDefinition | null {
    return Object.prototype.hasOwnProperty.call(
        SITE_BUILDER_RUNTIME_CONTRACTS,
        type,
    )
        ? SITE_BUILDER_RUNTIME_CONTRACTS[
            type as SiteBuilderInnerBlockType
        ]
        : null;
}

export function siteBuilderRuntimeSupportsHost(
    type: SiteBuilderInnerBlockType,
    host: SiteBuilderRuntimeHost,
): boolean {
    return SITE_BUILDER_RUNTIME_CONTRACTS[type].supportedHosts.includes(host);
}

export function siteBuilderRuntimeBlockTypesForHost(
    host: SiteBuilderRuntimeHost,
): SiteBuilderInnerBlockType[] {
    return SITE_BUILDER_RUNTIME_BLOCK_TYPES.filter((type) => (
        siteBuilderRuntimeSupportsHost(type, host)
    ));
}

export function siteBuilderRuntimeMissingCapabilities(
    type: SiteBuilderInnerBlockType,
    supportedCapabilities: readonly SiteBuilderRuntimeCapability[] | undefined,
): SiteBuilderRuntimeCapability[] {
    const supported = new Set(supportedCapabilities ?? []);

    return SITE_BUILDER_RUNTIME_CONTRACTS[type].requiredCapabilities.filter(
        (capability) => !supported.has(capability),
    );
}

export function siteBuilderRuntimeSupports(
    type: SiteBuilderInnerBlockType,
    supportedCapabilities: readonly SiteBuilderRuntimeCapability[] | undefined,
): boolean {
    return siteBuilderRuntimeMissingCapabilities(
        type,
        supportedCapabilities,
    ).length === 0;
}

/** Returns blocks for which a renderer did not declare any support entry. */
export function siteBuilderRuntimeMissingBlockSupport(
    support: SiteBuilderRuntimeSupport,
    host?: SiteBuilderRuntimeHost,
): SiteBuilderInnerBlockType[] {
    return SITE_BUILDER_RUNTIME_BLOCK_TYPES.filter((type) => (
        (host === undefined || siteBuilderRuntimeSupportsHost(type, host))
        && !Object.prototype.hasOwnProperty.call(support, type)
    ));
}

/** Returns every missing block capability, including completely absent blocks. */
export function siteBuilderRuntimeSupportGaps(
    support: SiteBuilderRuntimeSupport,
    host?: SiteBuilderRuntimeHost,
): SiteBuilderRuntimeSupportGap[] {
    return SITE_BUILDER_RUNTIME_BLOCK_TYPES.flatMap((type) => {
        if (host !== undefined && !siteBuilderRuntimeSupportsHost(type, host)) {
            return [];
        }

        const contract = SITE_BUILDER_RUNTIME_CONTRACTS[type];
        const missingCapabilities = siteBuilderRuntimeMissingCapabilities(
            type,
            support[type],
        );

        return missingCapabilities.length > 0
            ? [{
                behaviorKind: contract.behaviorKind,
                missingCapabilities,
                type,
            }]
            : [];
    });
}
